/**
 * ریزبینیِ یک ردیفِ رجیستری — «اگر این منبع را برداریم، چه از دست می‌رود؟»
 *
 * جدولِ اصلی می‌گوید کدام ردیف را نگاه کن؛ این‌جا می‌گوید چه چیزی آن‌جاست.
 * سه لایه، و ترتیبشان عمدی است:
 *   ۱. در این لود چه گذشت (شبکه و قیف)
 *   ۲. هر فیلد چقدر خالی است و چقدر انحصاری
 *   ۳. **پنج مثالِ واقعی** — تنها چیزی که تا حالا باگِ واقعی پیدا کرده
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  registryRepo,
  type RegistryField, type RegistryRow, type RegistrySample, type RegistrySampleKind,
} from '@/core/data/registryRepo';
import { Two, fmtNum as fmt } from '@/ui/Num';
import { Hint } from '@/ui/Hint';

/**
 * دو مقدارِ **غیرعددی** در یک خانه — میلی‌ثانیه، حجم، درصد.
 *
 * ⚠ چرا `Two` این کار را نمی‌کند: آن یکی خودش عدد را قالب می‌زند، و
 * «۲٫۲ ثانیه» یا «۱٫۷ گیگ» را نمی‌شود از `toLocaleString` درآورد.
 */
const Pair = ({ d10, run }: { d10: string; run: string }) => (
  <span className="two" title={`۱۰ روز: ${d10} · آخرین اجرا: ${run}`}>
    {d10}<span className="dim"> / {run}</span>
  </span>
);

const pct = (a: number, b: number) => (b > 0 ? `${Math.round((a / b) * 100)}٪` : '—');

const ms = (n: number | null | undefined) => {
  if (typeof n !== 'number' || n <= 0) return '—';
  if (n < 1000) return `${Math.round(n)}ms`;
  return `${(n / 1000).toFixed(1)}s`;
};

const bytes = (n: number | null | undefined) => {
  if (typeof n !== 'number' || n <= 0) return '—';
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * ⚠ `starts_at` یک لحظه‌ی **UTC** است و `timeZone` این‌جا اجباری است، نه
 * تزئین. رندر کردنش با منطقه‌ی زمانیِ مرورگر یک بار در صفحه‌ی لیبل‌زنیِ
 * کرالر دو لیبل را خراب کرد: «۱۹ سپتامبر ۰۰:۰۰ اتاوا» در تهران «۲۰ سپتامبر
 * ۰۲:۳۰» دیده می‌شد و ادمین بر اساسِ چیزی تصمیم گرفت که داده‌ی واقعی نبود.
 *
 * منطقه از شهرِ همان منبع می‌آید (`crawler.cities.timezone`). اگر معلوم
 * نبود UTC نشان داده می‌شود و **صریح گفته می‌شود** که UTC است — مبهم بودنِ
 * اعلام‌شده از دقتِ دروغین بهتر است.
 */
const stamp = (iso: string | null, tz: string | null) => {
  if (!iso) return '—';
  const zone = tz ?? 'UTC';
  const s = new Date(iso).toLocaleString('en-CA', {
    timeZone: zone, dateStyle: 'short', timeStyle: 'short',
  });
  return `${s} ${tz ? zone.split('/').pop() : 'UTC'}`;
};

const SAMPLE_KINDS: { kind: RegistrySampleKind; label: string; why: string }[] = [
  { kind: 'empty', label: 'خالی', why: 'رویدادهایی که این فیلدشان خالی مانده.' },
  { kind: 'sole', label: 'انحصاری', why: 'فقط این منبع این فیلد را داشت — با حذفش خالی می‌شود.' },
  { kind: 'lost', label: 'باخته', why: 'claim داشت و دیگری برنده شد. هر دو مقدار کنارِ هم.' },
  { kind: 'conflict', label: 'متعارض', why: 'merger روی این فیلد تعارض دید.' },
];

function SampleTable({ rows, tz }: { rows: RegistrySample[]; tz: string | null }) {
  if (!rows.length) return <p className="dim">نمونه‌ای نیست.</p>;
  return (
    <div className="scrollx">
      <table className="tbl">
        <thead>
          <tr>
            <th>عنوان</th><th>شروع</th>
            <th>مقدارِ ما</th><th>مقدارِ برنده</th><th>برنده</th><th>لینک</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const differs = r.our_value != null && r.winning_value != null
              && r.our_value !== r.winning_value;
            return (
              <tr key={r.cluster_id}>
                <td className="ltr">{r.title ?? <span className="dim">—</span>}</td>
                <td className="num">{stamp(r.starts_at, tz)}</td>
                <td className={`ltr${differs ? ' bad' : ''}`}>
                  {r.our_value ?? <span className="dim">—</span>}
                </td>
                {/* وقتی دو مقدار یکی‌اند تکرارش نمی‌شود: ستونی که در هر ردیف
                    همان رشته را دو بار نشان بدهد، ردیفی را که واقعاً فرق
                    دارد از چشم می‌اندازد. */}
                <td className="ltr">
                  {differs ? r.winning_value : <span className="dim">همان</span>}
                </td>
                <td className="mono dim">{r.winner ?? '—'}</td>
                <td className="mono">
                  {r.url ? (
                    <a href={r.url} target="_blank" rel="noreferrer" dir="ltr">↗</a>
                  ) : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * حالتِ پیش‌فرض از خودِ ردیفِ فیلد درمی‌آید و ثابت نیست.
 *
 * چرا: با پیش‌فرضِ ثابتِ `sole`، اولین کلیک روی فیلدی مثل `origin_url` که
 * صفر تأمین‌کننده‌ی انحصاری دارد «نمونه‌ای نیست» می‌دهد — و آدم فکر می‌کند
 * ابزار کار نمی‌کند، نه اینکه آن فیلد این حالت را ندارد.
 */
const defaultKind = (fd: RegistryField): RegistrySampleKind => {
  if (fd.sole_provider > 0) return 'sole';
  if (fd.lost > 0) return 'lost';
  if (fd.empty > 0) return 'empty';
  return 'conflict';
};

function Samples({ source, field, tz, initial }: {
  source: string; field: string; tz: string | null; initial: RegistrySampleKind;
}) {
  const [kind, setKind] = useState<RegistrySampleKind>(initial);
  const q = useQuery({
    queryKey: ['regSamples', source, field, kind],
    queryFn: () => registryRepo.samples(source, field, kind, 5),
  });
  const active = SAMPLE_KINDS.find((k) => k.kind === kind)!;

  return (
    <div className="detail">
      <div className="chiprow" style={{ marginBottom: 6 }}>
        <span className="dim">
          <span className="mono">{field}</span> —
        </span>
        {SAMPLE_KINDS.map((k) => (
          <button key={k.kind} type="button"
            className={`chip${kind === k.kind ? ' on' : ''}`}
            onClick={() => setKind(k.kind)}>{k.label}</button>
        ))}
      </div>
      <p className="dim" style={{ fontSize: 12.5, marginBottom: 6 }}>{active.why}</p>
      {q.isLoading ? <p className="dim">…</p>
        : q.error ? <p className="err mono">{String(q.error)}</p>
        : <SampleTable rows={q.data?.samples ?? []} tz={tz} />}
    </div>
  );
}

/** بلوکِ عدد+برچسبِ فشرده، برای جدولِ آمارِ لود. */
const Cell = ({ k, v, bad, help }: {
  k: string; v: React.ReactNode; bad?: boolean; help?: string;
}) => (
  <div>
    <div className={`n${bad ? ' bad' : ''}`}
      style={{ fontSize: 17, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
      {v}
    </div>
    <div className="k" style={{ fontSize: 11.5, color: 'var(--ink2)' }}>
      <Hint text={k} help={help} />
    </div>
  </div>
);

/**
 * ⚠️ «خوابِ خودمان» تنها عددی در این کارت است که **کارِ ما**ست و نه کارِ
 * سایت، و از نامش هم پیدا نیست. کنارِ «انتظارِ شبکه» می‌نشیند دقیقاً برای
 * همین مقایسه، ولی تا وقتی کسی نداند این یکی یعنی چه، آن مقایسه اتفاق
 * نمی‌افتد.
 */
const WAIT_HELP =
  'زمانی که خودمان عمداً پشتِ در صبر کردیم — نه زمانی که سایت طول داد. کرالر '
  + 'بینِ دو درخواست به یک سایت فاصله می‌گذارد تا رویش فشار نیاورد.\n\n'
  + 'مثال: عددِ کناری «انتظارِ شبکه» است. اگر آن ۵۰ میلی‌ثانیه باشد و این '
  + '۹۳۷، سایت اصلاً کند نیست و کلِ کندی مالِ سیاستِ خودمان است. این دو، دو '
  + 'مسئله با دو درمانِ کاملاً متفاوت‌اند.\n\n'
  + 'قدمِ بعدی: اگر این عدد بزرگ است و منبع ارزشش را دارد، فاصله‌ی مجازش '
  + '(`min_interval`) را کم کن.';

const AGGREGATOR_HELP =
  'این سایت خودش برگزارکننده نیست — رویدادهای جاهای دیگر را جمع می‌کند.\n\n'
  + 'مثال: Eventbrite یا Ticketmaster. «کنسرتِ سالن ملی» را دارند، ولی خودِ '
  + 'رویداد مالِ سالن ملی است.\n\n'
  + 'چرا نشانش می‌دهیم: برای تجمیع‌کننده، کرالر یک قدمِ اضافه می‌رود و دنبالِ '
  + 'لینکِ اصلیِ رویداد می‌گردد، تا کاربر به صفحه‌ی خودِ برگزارکننده برسد نه '
  + 'به واسطه. ستونِ «reference_url» پایینِ همین کارت نتیجه‌اش را نشان می‌دهد.';

export function SourceCard({ row, tz }: { row: RegistryRow; tz: string | null }) {
  const [openField, setOpenField] = useState<string | null>(null);

  // `null` یعنی «آخرین اجرا» — انتخابگرِ لود از مایگریشنِ ۰۱۰۸ حذف شد.
  const fields = useQuery({
    queryKey: ['regFields', row.source_id],
    queryFn: () => registryRepo.fields(null, row.source_id),
  });

  const l = row.load;
  const s = row.state;
  const hostRow = (title: string, h: Record<string, number>) => {
    const entries = Object.entries(h ?? {}).filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1]);
    if (!entries.length) return null;
    return (
      <p style={{ marginTop: 4 }}>
        <span className="dim">{title}: </span>
        {entries.map(([k, v]) => (
          <span key={k} className="chip" style={{ marginInlineEnd: 4 }}>
            {k} {fmt(v)}
          </span>
        ))}
      </p>
    );
  };

  return (
    <div className="detail">
      {/* ── هویت ── */}
      <div className="chiprow" style={{ marginBottom: 8 }}>
        <b className="mono">{row.source_id}</b>
        {row.label ? <span className="dim">{row.label}</span> : null}
        {row.tier ? <span className="chip">tier {row.tier}</span> : null}
        {row.trust != null ? <span className="chip">trust {row.trust}</span> : null}
        {row.extractor ? <span className="chip mono">{row.extractor}</span> : null}
        {row.aggregator
          ? <span className="chip hint" title={AGGREGATOR_HELP}>تجمیع‌کننده</span> : null}
        {!row.enabled ? <span className="chip warn">خاموش</span> : null}
        {/* ⚠ کلیدِ ادغامِ درفت و منبع «قرارداد» است نه ارجاع: دو پیش‌نویسِ
            هم‌نام در دو شهر می‌توانند اشتباهی یک ردیف شوند. این تنها جایی
            است که چنین ادغامی دیده می‌شود. */}
        {row.merged_from.length > 1 ? (
          <span className="chip warn mono">ادغامِ {row.merged_from.join(' + ')}</span>
        ) : null}
      </div>

      {/* ── ۱ · جریان: ۱۰ روزِ اخیر / همین اجرا ── */}
      <h3>جریان <span className="dim" style={{ fontSize: 12, fontWeight: 400 }}>
        — ۱۰ روزِ اخیر / آخرین اجرا</span></h3>
      <div className="grid n4" style={{ marginTop: 6 }}>
        <Cell k="درخواست" v={<Two v={l.requests} />} />
        <Cell k="گرفته‌شده" v={<Two v={l.fetched} />} />
        <Cell k="۳۰۴ بدونِ تغییر" v={<Two v={l.unchanged_304} />} />
        <Cell k="خطا / مسدود" v={<><Two v={l.errors} bad /> · <Two v={l.blocked} bad /></>} />
      </div>
      <div className="grid n4" style={{ marginTop: 8 }}>
        {/* ⚠ `net_ms` و `wait_ms` عمداً دو ستون‌اند: «سایت کند است» و «ما
            پشتِ min_interval خوابیدیم» دو مسئله با دو درمانِ متفاوت‌اند.
            آزمونِ سیم‌کشی ۵۰ms در برابرِ ۹۳۷ms را نشان داد. */}
        <Cell k="انتظارِ شبکه" v={<Pair d10={ms(l.net_ms.d10)} run={ms(l.net_ms.run)} />} />
        <Cell k="خوابِ خودمان" help={WAIT_HELP}
          v={<Pair d10={ms(l.wait_ms.d10)} run={ms(l.wait_ms.run)} />} />
        <Cell k="حجم" v={<Pair d10={bytes(l.bytes.d10)} run={bytes(l.bytes.run)} />} />
        {/* مخرجِ هر ستون مالِ خودش است: سهمِ ده‌روزه از شبکه‌ی ده روز. */}
        <Cell k="سهم از شبکه" v={<Pair d10={`${l.pct_fetch_time.d10}٪`}
          run={`${l.pct_fetch_time.run}٪`} />} />
      </div>
      <div className="grid n4" style={{ marginTop: 8 }}>
        <Cell k="کاندیدای تازه" v={<Two v={l.candidates_new} />} />
        <Cell k="ادعا" v={<Two v={l.claims} />} />
        <Cell k="mention" v={<Two v={l.mentions} />} />
        <Cell k="منتشرشده" v={<Two v={l.published} />} />
      </div>
      {/* ⚠ کلیدهای این دو شمارنده از اجرایی به اجرای دیگر فرق می‌کنند، پس
          به‌جای دو ستون، دو خط: بالایی ۱۰ روز، پایینی همین اجرا. */}
      {[
        { label: '۱۰ روزِ اخیر', m: l.matches?.d10, d: l.publish?.d10 },
        { label: 'آخرین اجرا', m: l.matches?.run, d: l.publish?.run },
      ].map((line) => (
        Object.keys(line.m ?? {}).length || Object.keys(line.d ?? {}).length ? (
          <p key={line.label} className="dim" style={{ marginTop: 6, fontSize: 12.5 }}>
            <b>{line.label}:</b>{' '}
            {Object.entries(line.m ?? {}).map(([k, v]) => `${k} ${fmt(v)}`).join(' · ')}
            {Object.keys(line.d ?? {}).length ? ' — حکم: ' : ''}
            {Object.entries(line.d ?? {}).map(([k, v]) => `${k} ${fmt(v)}`).join(' · ')}
          </p>
        ) : null
      ))}
      <p className="dim" style={{ marginTop: 4, fontSize: 12.5 }}>
        بازکلیدشده: <Two v={l.churned} /> · کاندیدای دوباره‌دیده‌شده:{' '}
        <Two v={l.candidates_seen} /> · فیلدهای ادعا: <Two v={l.claim_fields} />
      </p>

      {/* ⚠ «اندازه‌گیری نشد» با «صفر» یکی نیست: صفر گذاشتن یعنی گران‌ترین
          منبع در لودی که فقط merge بوده مجانی‌ترین به نظر برسد. */}
      <p className="note">
        {row.cost.measured === false
          ? 'هزینه در آخرین اجرا اندازه‌گیری نشده — به شبکه نزده. این با «مجانی بود» یکی نیست.'
          : `هر منتشرشده: ${ms(row.cost.ms_per_published)} و `
            + `${fmt(row.cost.requests_per_published)} درخواست · `
            + `هر فیلدِ انحصاری: ${ms(row.cost.ms_per_sole_field)}`}
      </p>

      {/* ── ۲ · لینک به کجا می‌رود ── */}
      <h3 style={{ marginTop: 12 }}>لینک‌ها</h3>
      {/* ⚠ دو محورِ جدا و هر دو لازم. با فقط یکی، «صفر فیسبوکی داریم»
          درست ولی آرامش‌بخشِ نادرست بود: `reference_url` چیزی است که به
          کاربر نشان داده می‌شود، `event_url` جایی که پوسیدگیِ لینک
          اتفاق می‌افتد. */}
      {hostRow('reference_url — چیزی که کاربر می‌بیند', s.ref_hosts)}
      {hostRow('event_url — جایی که لینک می‌پوسد', s.event_hosts)}

      {/* ── ۳ · فیلدها ── */}
      <h3 style={{ marginTop: 12 }}>فیلدها</h3>
      {fields.isLoading ? <p className="dim">…</p> : null}
      {fields.error ? <p className="err mono">{String(fields.error)}</p> : null}
      {fields.data?.fields ? (
        <div className="scrollx">
          <table className="tbl">
            <thead>
              <tr>
                <th>فیلد</th><th className="num">پرشده</th><th className="num">خالی</th>
                <th className="num">تنها تأمین‌کننده</th>
                <th className="num">برنده</th><th className="num">باخته</th><th />
              </tr>
            </thead>
            <tbody>
              {fields.data.fields.map((fd) => (
                <React.Fragment key={fd.field}>
                  <tr>
                    <td className="mono">{fd.field}</td>
                    <td className="num">
                      {fmt(fd.filled)} <span className="dim">{pct(fd.filled, fd.events)}</span>
                    </td>
                    {/* ⚠ «خالی» از خروجی خوانده می‌شود نه از نبودِ ورودی:
                        فیلدی که claim داشته ولی merge کنارش گذاشته، برای
                        کاربر خالی است. */}
                    <td className={`num${fd.empty === fd.events ? ' bad' : ''}`}>
                      {fmt(fd.empty)}
                    </td>
                    <td className="num">{fmt(fd.sole_provider)}</td>
                    <td className="num">{fmt(fd.won)}</td>
                    <td className="num">{fmt(fd.lost)}</td>
                    <td>
                      <button type="button" className="linkbtn"
                        onClick={() => setOpenField(openField === fd.field ? null : fd.field)}>
                        {openField === fd.field ? 'بستن' : 'نمونه'}
                      </button>
                    </td>
                  </tr>
                  {openField === fd.field ? (
                    <tr>
                      <td colSpan={7} style={{ padding: 0 }}>
                        <Samples source={row.source_id} field={fd.field} tz={tz}
                          initial={defaultKind(fd)} />
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
